import { useEffect, useMemo, useState } from "react";
import { EmptyState, LoadingState } from "../../../components/ui";
import { fetchApproverDropdownOptions } from "../../../services/productVendorService";
import { getTicketAudit } from "../../../services/ticketTrackerService";

import { TicketStatusBadge } from "./TicketStatusBadge";

import { formatTicketProgress } from "./TicketFormFields";

const LOG_PAGE_SIZE = 20;

function displayValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function formatDateTime(iso) {
  if (!iso) return "";
  const date = new Date(String(iso).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(iso);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${dd} ${months[date.getMonth()]} ${date.getFullYear()} ${hh}:${mm}`;
}

function fieldOr(log, keys) {
  for (const key of keys) {
    if (log?.[key] != null && log[key] !== "") return log[key];
  }
  return null;
}

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

function looksLikeId(value) {
  const s = String(value || "").trim();
  if (!s) return false;
  return /^\d+$/.test(s) || /^EMP/i.test(s);
}

function humanizeLabel(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function resolveAuthorName(log, employeeMap) {
  const updatedByName = fieldOr(log, ["updatedByName", "updated_by_name"]);
  if (updatedByName) return String(updatedByName);
  const updatedBy = fieldOr(log, ["updatedBy", "updated_by"]);
  if (updatedBy != null) return employeeMap.get(String(updatedBy)) || String(updatedBy);
  const named = fieldOr(log, [
    "actorName",
    "authorName",
    "changedByName",
    "updatedByName",
    "createdByName",
    "userName",
    "fullName",
    "name",
  ]);
  if (named) return String(named);

  const actorLike = fieldOr(log, ["updatedBy", "updated_by", "actor", "author", "changedBy", "createdBy"]);
  if (actorLike != null && !looksLikeId(actorLike)) return String(actorLike);

  const idLike = fieldOr(log, [
    "updatedBy",
    "updated_by",
    "actorId",
    "userId",
    "empId",
    "employeeId",
    "actor",
    "author",
    "changedBy",
    "updatedBy",
    "createdBy",
  ]);
  if (idLike != null) {
    const key = String(idLike);
    if (employeeMap.has(key)) return employeeMap.get(key);
    return key;
  }
  return "Unknown";
}

function extractChangeRows(log) {
  const rows = [];
  const lists = [
    log?.changes,
    log?.changeList,
    log?.fieldChanges,
    log?.updatedFields,
    log?.diff,
    log?.details,
    log?.auditDetails,
  ];

  lists.forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      if (!item || typeof item !== "object") return;
      const field = fieldOr(item, ["field", "fieldName", "attribute", "name", "property", "key"]);
      const from = fieldOr(item, ["oldValue", "from", "previousValue", "oldStatus", "before"]);
      const to = fieldOr(item, ["newValue", "to", "currentValue", "newStatus", "after"]);
      if (field || from != null || to != null) {
        rows.push({ field, from, to });
      }
    });
  });

  // Single-field change on the root audit row
  const rootField = fieldOr(log, ["field", "fieldName", "attribute", "changedField", "property"]);
  const rootFrom = fieldOr(log, [
    "oldValue",
    "from",
    "previousValue",
    "oldStatus",
    "assignedFrom",
    "before",
  ]);
  const rootTo = fieldOr(log, [
    "newValue",
    "to",
    "currentValue",
    "newStatus",
    "assignedTo",
    "after",
  ]);
  if (rootField || rootFrom != null || rootTo != null) {
    rows.push({ field: rootField, from: rootFrom, to: rootTo });
  }

  return rows;
}

function mapAuditEntry(log, idx, employeeMap) {
  const id = fieldOr(log, ["auditId", "logId", "id"]) ?? idx;
  const updatedDate = fieldOr(log, ["updatedDate", "updated_date", "updatedOn", "updatedAt", "updated_at", "changedOn", "timestamp", "createdOn", "createdAt"]);
  const action = fieldOr(log, [
    "action",
    "event",
    "activity",
    "changeType",
    "operation",
    "requestAction",
  ]);
  const actor = resolveAuthorName(log, employeeMap);
  const notes = fieldOr(log, [
    "notes",
    "remarks",
    "remark",
    "comment",
    "comments",
    "message",
    "description",
    "summary",
  ]);
  const changeRows = extractChangeRows(log);

  const status = fieldOr(log, ["status", "newStatus", "currentStatus", "new_status"])
    ?? changeRows.find((row) => String(row.field).toLowerCase() === "status")?.to;
  return { id, updatedDate, status, action, actor, notes, changeRows, progressPercentage: log?.progressPercentage };
}

function buildDiscussionBody(entry) {
  const parts = [];
  const actionText = displayValue(entry.action);
  const isGenericUpdate =
    !actionText ||
    /^(request\s*)?(updated|update|modified|changed|edit(ed)?)$/i.test(actionText.trim());

  if (entry.changeRows?.length) {
    entry.changeRows.forEach((row) => {
      const fieldLabel = row.field ? humanizeLabel(row.field) : "Value";
      const fromText = displayValue(row.from);
      const toText = displayValue(row.to);
      if (fromText || toText) {
        parts.push(`${fieldLabel}: ${fromText || "—"} → ${toText || "—"}`);
      } else if (row.field) {
        parts.push(`Updated ${fieldLabel}`);
      }
    });
  }

  if (entry.notes) {
    parts.push(displayValue(entry.notes));
  }

  if (parts.length === 0) {
    if (actionText && !isGenericUpdate) {
      parts.push(humanizeLabel(actionText));
    } else if (actionText) {
      parts.push("Request was updated");
    }
  } else if (actionText && !isGenericUpdate) {
    parts.unshift(humanizeLabel(actionText));
  }

  return parts.filter(Boolean).join("\n");
}

/**
 * Discussion-style audit history (chat/comment thread).
 */
export function TicketAuditHistory({ requestId, refreshKey = 0 }) {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [last, setLast] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchApproverDropdownOptions({ size: 200 })
      .then((opts) => {
        if (!cancelled) setEmployees(Array.isArray(opts) ? opts : []);
      })
      .catch(() => {
        if (!cancelled) setEmployees([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const employeeMap = useMemo(() => {
    const map = new Map();
    employees.forEach((emp) => {
      if (emp?.value != null) map.set(String(emp.value), emp.label || String(emp.value));
    });
    return map;
  }, [employees]);

  useEffect(() => {
    if (!requestId) {
      setLogs([]);
      setPage(0);
      setLast(true);
      setError("");
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      setLogs([]);
      setPage(0);
      try {
        const result = await getTicketAudit(requestId, { page: 0, size: LOG_PAGE_SIZE });
        if (cancelled) return;
        setLogs(result.items || []);
        setPage(result.page);
        setLast(result.last);
      } catch (err) {
        if (cancelled) return;
        setLogs([]);
        setError(err?.message || "Unable to load discussion history.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [requestId, refreshKey]);

  const handleLoadMore = async () => {
    if (loadingMore || last || !requestId) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const result = await getTicketAudit(requestId, {
        page: nextPage,
        size: LOG_PAGE_SIZE,
      });
      setLogs((prev) => [...prev, ...(result.items || [])]);
      setPage(result.page);
      setLast(result.last);
    } catch (err) {
      console.error("Failed to load more discussion rows:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const entries = logs.map((log, idx) => mapAuditEntry(log, idx, employeeMap));

  return (
    <div className="bg-white rounded-xl shadow-[0_8px_24px_-16px_rgba(30,51,131,0.3)] border border-white/60 overflow-hidden p-3 sm:p-4 mb-4">
      <h3 className="text-sm font-semibold text-foreground mb-3">Discussion</h3>

      {loading ? (
        <LoadingState message="Loading discussion…" className="py-6" />
      ) : error && entries.length === 0 ? (
        <EmptyState title="Unable to load discussion" message={error} className="py-6" />
      ) : entries.length === 0 ? (
        <EmptyState
          title="No discussion yet"
          message="Updates and comments will appear here."
          className="py-6"
        />
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {entries.map((entry, idx) => {
              const body = buildDiscussionBody(entry);
              const author = entry.actor || "Unknown";
              const when = formatDateTime(entry.updatedDate);
              return (
                <div
                  key={`${entry.id}-${idx}`}
                  className="flex items-start gap-3 rounded-xl border border-(--color-text-border) bg-workspace/15 p-3 sm:p-4"
                >
                  <div className="h-8 w-8 shrink-0 rounded-full bg-primary/10 text-primary grid place-items-center text-[10px] font-bold">
                    {getInitials(author)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                      <span className="text-xs font-semibold text-foreground"><span className="font-normal per-file-hint">Updated by </span>{author}</span>
                      {entry.status ? <TicketStatusBadge status={displayValue(entry.status)} /> : <span className="text-[10px] per-file-hint">Status unavailable</span>}
                      <span className="text-[10px] per-file-hint">{formatTicketProgress(entry.progressPercentage)}</span>
                      <span className="text-[10px] per-file-hint sm:ml-auto">Updated: {when || "Date unavailable"}</span>
                    </div>
                    {body ? (
                      <p className="mt-3 border-t border-(--color-text-border) pt-3 text-xs text-foreground whitespace-pre-wrap wrap-break-word leading-relaxed">
                        {body}
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          {!last ? (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                disabled={loadingMore}
                onClick={handleLoadMore}
                className="h-8 px-3 rounded-lg border border-(--color-text-border) text-xs font-medium text-primary hover:bg-workspace/40 disabled:opacity-50"
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
